import { simplifyCoordinatesForZoom } from './routeSimplification'

export class RouteRenderer {
  private map: mapboxgl.Map
  private renderedLayerIds: string[] = []
  private isDestroyed: boolean = false
  private currentRouteId: string | null = null
  private renderTimeout: NodeJS.Timeout | null = null
  private routeCache: Map<string, { layerIds: string[], timestamp: number }> = new Map()
  private readonly CACHE_DURATION = 30000 // 30 seconds cache
  private hoveredRouteId: string | null = null
  private selectedRouteId: string | null = null

  constructor(map: mapboxgl.Map) {
    this.map = map
  }

  destroy() {
    this.isDestroyed = true
    this.clearRenderedRoute()
    this.map = null as any
  }

  clearRenderedRoute(forceRemove: boolean = false) {
    if (!this.map || this.isDestroyed) return
    
    // Cancel any pending render
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout)
      this.renderTimeout = null
    }
    
    if (forceRemove) {
      // Actually remove all layers from cache
      this.routeCache.forEach((cachedRoute, routeId) => {
        cachedRoute.layerIds.forEach(layerId => {
          try {
            if (this.map.getLayer(layerId)) {
              this.map.removeLayer(layerId)
            }
            if (this.map.getSource(layerId)) {
              this.map.removeSource(layerId)
            }
          } catch (error) {
            console.warn('Error removing layer/source:', layerId, error)
          }
        })
      })
      // Also remove any layers from renderedLayerIds that might not be in cache
      this.renderedLayerIds.forEach(layerId => {
        try {
          if (this.map.getLayer(layerId)) {
            this.map.removeLayer(layerId)
          }
          if (this.map.getSource(layerId)) {
            this.map.removeSource(layerId)
          }
        } catch (error) {
          console.warn('Error removing layer/source:', layerId, error)
        }
      })
      this.renderedLayerIds = []
      this.routeCache.clear()
    } else {
      // Just hide all routes
      this.hideAllRoutes()
    }
    
    this.currentRouteId = null
    this.hoveredRouteId = null
    this.selectedRouteId = null
  }
  
  private cleanupCache() {
    const now = Date.now()
    const toDelete: string[] = []
    
    this.routeCache.forEach((value, key) => {
      // Don't clean up the currently selected or hovered route
      if (key === this.selectedRouteId || key === this.hoveredRouteId) {
        return
      }
      
      if (now - value.timestamp > this.CACHE_DURATION) {
        // Remove the layers
        value.layerIds.forEach(layerId => {
          try {
            if (this.map.getLayer(layerId)) {
              this.map.removeLayer(layerId)
            }
            if (this.map.getSource(layerId)) {
              this.map.removeSource(layerId)
            }
          } catch (error) {
            console.warn('Error removing cached layer/source:', layerId, error)
          }
        })
        toDelete.push(key)
      }
    })
    
    toDelete.forEach(key => this.routeCache.delete(key))
  }
  
  private hideAllRoutes() {
    this.renderedLayerIds.forEach(layerId => {
      try {
        if (this.map.getLayer(layerId)) {
          this.map.setLayoutProperty(layerId, 'visibility', 'none')
        }
      } catch (error) {
        console.warn('Error hiding layer:', layerId, error)
      }
    })
  }
  
  private hideOtherRoutes(exceptRouteId: string) {
    // Hide all cached routes except the specified one
    this.routeCache.forEach((value, routeId) => {
      if (routeId !== exceptRouteId) {
        value.layerIds.forEach(layerId => {
          try {
            if (this.map.getLayer(layerId)) {
              this.map.setLayoutProperty(layerId, 'visibility', 'none')
            }
          } catch (error) {
            console.warn('Error hiding layer:', layerId, error)
          }
        })
      }
    })
  }
  
  clearHoveredRoute() {
    if (this.hoveredRouteId && this.hoveredRouteId !== this.selectedRouteId) {
      const cachedRoute = this.routeCache.get(this.hoveredRouteId)
      if (cachedRoute) {
        cachedRoute.layerIds.forEach(layerId => {
          try {
            if (this.map.getLayer(layerId)) {
              this.map.setLayoutProperty(layerId, 'visibility', 'none')
            }
          } catch (error) {
            console.warn('Error hiding hover layer:', layerId, error)
          }
        })
      }
    }
    this.hoveredRouteId = null
  }

  renderRoute(route: any, preserveExisting: boolean = false, isHover: boolean = false) {
    if (!this.map || this.isDestroyed) return
    
    const routeId = route?.id || route?.firebaseRoute?.id
    if (!routeId) {
      console.warn('Route has no ID, cannot cache')
      return
    }
    
    // If this is a hover, track it
    if (isHover) {
      this.hoveredRouteId = routeId
    } else {
      this.selectedRouteId = routeId
    }
    
    // Check if this route's layers are already visible
    const cachedRoute = this.routeCache.get(routeId)
    if (cachedRoute) {
      // Route is cached, just make sure layers are visible
      cachedRoute.layerIds.forEach(layerId => {
        if (this.map.getLayer(layerId)) {
          this.map.setLayoutProperty(layerId, 'visibility', 'visible')
        }
      })
      console.log('🗺️ Route made visible from cache:', routeId)
      return
    }
    
    // Cancel any pending render
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout)
      this.renderTimeout = null
    }
    
    // Immediate render for better responsiveness
    this._performRenderRoute(route, preserveExisting, isHover)
  }
  
  private _performRenderRoute(route: any, preserveExisting: boolean, isHover: boolean) {
    if (!this.map || this.isDestroyed) return
    
    const routeId = route?.id || route?.firebaseRoute?.id
    
    console.log('🗺️ renderRouteOnMap called with route:', route?.name || 'Unknown')
    console.log('🗺️ route data structure:', route)
    
    // Instead of clearing, hide non-relevant routes
    if (!preserveExisting) {
      this.hideOtherRoutes(routeId)
    }
    
    // Update current route ID
    this.currentRouteId = routeId
    
    // Get all routes (segments)
    let allRoutes: any[] = []
    
    // Check if it's a direct Firebase route object or wrapped route object
    if (route.routes && route.routes.length > 0) {
      // Multi-route - render all segments (direct Firebase route)
      allRoutes = route.routes
    } else if (route.coordinates && route.coordinates.length > 0) {
      // Single route (direct Firebase route)
      allRoutes = [{
        coordinates: route.coordinates,
        color: route.color || '#10b981'  // Single routes should have color at route level
      }]
    } else if (route.firebaseRoute?.routes && route.firebaseRoute.routes.length > 0) {
      // Multi-route - render all segments (wrapped route)
      allRoutes = route.firebaseRoute.routes
    } else if (route.firebaseRoute?.coordinates && route.firebaseRoute.coordinates.length > 0) {
      // Single route (wrapped route)
      allRoutes = [{
        coordinates: route.firebaseRoute.coordinates,
        color: route.firebaseRoute.color || route.color || '#10b981'  // Check firebaseRoute.color first
      }]
    }
    
    if (allRoutes.length === 0) return
    
    // Clear old cache entries
    this.cleanupCache()
    
    // Track new layer IDs for this route
    const newLayerIds: string[] = []
    
    // Render each route segment
    allRoutes.forEach((routeData: any, routeIndex: number) => {
      const originalCoordinates = routeData.coordinates
      if (!originalCoordinates || originalCoordinates.length === 0) return
      
      // Simplify coordinates based on current zoom level, preserving surface transitions if available
      const currentZoom = this.map.getZoom()
      const surfaces = routeData.surfaces || routeData.surfaceTypes
      const simplifiedCoordinates = simplifyCoordinatesForZoom(
        originalCoordinates, 
        currentZoom, 
        surfaces
      )
      
      console.log(`🗺️ Route segment ${routeIndex} - Original: ${originalCoordinates.length} points, Simplified: ${simplifiedCoordinates.length} points (zoom: ${currentZoom.toFixed(1)})`)
      
      const routeColor = routeData.color || '#10b981'
      
      // Check if route has unpaved segments
      if (routeData.unpavedSegments && routeData.unpavedSegments.length > 0) {
        // TODO: Also simplify unpaved segments
        const layerIds = this.renderRouteWithSurface(routeId, routeIndex, simplifiedCoordinates, routeColor, routeData.unpavedSegments)
        newLayerIds.push(...layerIds)
      } else {
        const layerIds = this.renderSolidRoute(routeId, routeIndex, simplifiedCoordinates, routeColor)
        newLayerIds.push(...layerIds)
      }
    })
    
    // Cache the rendered layers
    this.routeCache.set(routeId, {
      layerIds: newLayerIds,
      timestamp: Date.now()
    })
  }

  private renderRouteWithSurface(
    routeId: string, 
    routeIndex: number, 
    coordinates: [number, number][], 
    routeColor: string, 
    unpavedSegments: [number, number][][]
  ): string[] {
    const layerIds: string[] = []
    // First add white stroke layer for the entire route
    const strokeLayerId = `route-hover-stroke-${routeId}-${routeIndex}`
    
    this.map.addSource(strokeLayerId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: coordinates
        }
      }
    })
    
    this.map.addLayer({
      id: strokeLayerId,
      type: 'line',
      source: strokeLayerId,
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#ffffff',
        'line-width': 6,
        'line-opacity': 1
      }
    })
    
    this.renderedLayerIds.push(strokeLayerId)
    layerIds.push(strokeLayerId)
    
    // Add the main route line
    const routeLayerId = `route-hover-${routeId}-${routeIndex}`
    
    this.map.addSource(routeLayerId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: coordinates
        }
      }
    })
    
    this.map.addLayer({
      id: routeLayerId,
      type: 'line',
      source: routeLayerId,
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': routeColor,
        'line-width': 4,
        'line-opacity': 0.8
      }
    })
    
    this.renderedLayerIds.push(routeLayerId)
    layerIds.push(routeLayerId)
    
    // Add unpaved overlays with dashed pattern
    unpavedSegments.forEach((segment: [number, number][], segmentIndex: number) => {
      const unpavedLayerId = `route-hover-unpaved-${routeId}-${routeIndex}-${segmentIndex}`
      
      this.map.addSource(unpavedLayerId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: segment
          }
        }
      })
      
      // Add dashed line for unpaved
      this.map.addLayer({
        id: unpavedLayerId,
        type: 'line',
        source: unpavedLayerId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': routeColor,
          'line-width': 4,
          'line-opacity': 0.8,
          'line-dasharray': [0.5, 2]
        }
      })
      
      this.renderedLayerIds.push(unpavedLayerId)
      layerIds.push(unpavedLayerId)
      
      // Add white dot overlay
      const overlayLayerId = `route-hover-overlay-${routeId}-${routeIndex}-${segmentIndex}`
      
      this.map.addSource(overlayLayerId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: segment
          }
        }
      })
      
      this.map.addLayer({
        id: overlayLayerId,
        type: 'line',
        source: overlayLayerId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#ffffff',
          'line-width': 2,
          'line-opacity': 1,
          'line-dasharray': [0.1, 2]
        }
      })
      
      this.renderedLayerIds.push(overlayLayerId)
      layerIds.push(overlayLayerId)
    })
    
    return layerIds
  }

  private renderSolidRoute(routeId: string, routeIndex: number, coordinates: [number, number][], routeColor: string): string[] {
    const layerIds: string[] = []
    // First add white stroke layer
    const strokeLayerId = `route-hover-stroke-${routeId}-${routeIndex}`
    
    this.map.addSource(strokeLayerId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: coordinates
        }
      }
    })
    
    this.map.addLayer({
      id: strokeLayerId,
      type: 'line',
      source: strokeLayerId,
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#ffffff',
        'line-width': 6,
        'line-opacity': 1
      }
    })
    
    this.renderedLayerIds.push(strokeLayerId)
    layerIds.push(strokeLayerId)
    
    // Then add the colored route line
    const routeLayerId = `route-hover-${routeId}-${routeIndex}`
    
    this.map.addSource(routeLayerId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: coordinates
        }
      }
    })
    
    this.map.addLayer({
      id: routeLayerId,
      type: 'line',
      source: routeLayerId,
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': routeColor,
        'line-width': 4,
        'line-opacity': 0.8
      }
    })
    
    this.renderedLayerIds.push(routeLayerId)
    layerIds.push(routeLayerId)
    
    return layerIds
  }
}